import { useEffect, useMemo, useState, useCallback } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { useLivePoll } from "../lib/useLivePoll";
import { Trophy, Eye, Share2, Zap } from "lucide-react";
import { SILK_COLOURS } from "../lib/constants";
import BottomSheet from "../components/BottomSheet";
import Skeleton from "../components/Skeleton";

function fmtPts(n) {
  const v = typeof n === "number" ? n : 0;
  return v.toFixed(2);
}

function PodiumColumn({ rank, entries, medal, heightClass }) {
  if (!entries || entries.length === 0) return <div className={`flex-1 ${heightClass}`} />;
  return (
    <div className={`flex-1 flex flex-col items-center justify-end`} data-testid={`podium-rank-${rank}`}>
      <div className="text-2xl mb-1">{medal}</div>
      <div className="flex flex-col items-center gap-1">
        {entries.map((e) => (
          <div key={e.user_id} className="flex flex-col items-center">
            <div className="w-11 h-11 rounded-full bg-white border-2 border-white shadow-md flex items-center justify-center text-2xl">
              {e.avatar_emoji}
            </div>
            <div className="text-[10px] font-bold mt-0.5 text-white text-center max-w-[80px] truncate">{e.display_name}</div>
          </div>
        ))}
      </div>
      <div className={`w-full mt-2 rounded-t-xl flex items-start justify-center pt-2 ${heightClass}`}
           style={{ background: rank === 1 ? "#c07800" : rank === 2 ? "#adb5bd" : "#a55a2f" }}>
        <div className="text-white font-display font-black">
          <div className="text-lg tabular-nums">{fmtPts(entries[0].total_points)}</div>
          <div className="text-[9px] uppercase tracking-widest text-center">pts</div>
        </div>
      </div>
      {entries.length > 1 && (
        <div className="text-[9px] font-black uppercase tracking-wider text-white/90 mt-1">Shared</div>
      )}
    </div>
  );
}

export default function LadderTab({ group }) {
  const [view, setView] = useState("season");
  const [week, setWeek] = useState(null);
  const [board, setBoard] = useState(null);
  const [bolter, setBolter] = useState(null);
  const [progress, setProgress] = useState(null);
  const [dates, setDates] = useState([]);
  const [progressDate, setProgressDate] = useState(null);
  const [flash, setFlash] = useState(false);
  const [openUser, setOpenUser] = useState(null);
  const [userPicks, setUserPicks] = useState([]);

  const load = useCallback(async () => {
    if (!group) return;
    try {
      const params = view === "week" ? { view: "week", week: week ?? 1 } : { view: "season" };
      const [{ data: lb }, { data: b }, { data: md }] = await Promise.all([
        api.get(`/groups/${group.id}/leaderboard`, { params }),
        api.get(`/groups/${group.id}/biggest-bolter`, { params: view === "week" ? { week: week ?? 1 } : {} }),
        api.get(`/groups/${group.id}/meeting-dates`),
      ]);
      setDates(md.meeting_dates);
      setProgressDate((cur) => cur || (md.meeting_dates[0]?.meeting_date));

      setBoard((prev) => {
        if (prev && prev.leaderboard_version !== lb.leaderboard_version) {
          setFlash(true);
          setTimeout(() => setFlash(false), 2200);
        }
        return lb;
      });
      setBolter(b.bolter);
    } catch (e) {
      // silent — polling
    }
  }, [group, view, week]);

  useLivePoll(load, 15000, [group?.id, view, week]);

  const loadProgress = useCallback(async () => {
    if (!group || !progressDate) return;
    try {
      const { data } = await api.get(`/groups/${group.id}/leaderboard/progress`, { params: { date: progressDate } });
      setProgress(data);
    } catch (_) {}
  }, [group, progressDate]);

  useEffect(() => { loadProgress(); }, [loadProgress]);

  // Build podium
  const podium = useMemo(() => {
    if (!board) return { r1: [], r2: [], r3: [] };
    const byRank = { 1: [], 2: [], 3: [] };
    board.entries.forEach((e) => {
      if (e.picks_made > 0 && byRank[e.rank]) byRank[e.rank].push(e);
    });
    return { r1: byRank[1], r2: byRank[2], r3: byRank[3] };
  }, [board]);

  const topPoints = useMemo(() => {
    if (!board) return 0;
    return Math.max(1, ...board.entries.map((e) => e.total_points));
  }, [board]);

  const openUserPicks = async (uid) => {
    setOpenUser(uid);
    try {
      const promises = dates.map((d) =>
        api.get(`/groups/${group.id}/picks/all`, { params: { date: d.meeting_date } })
      );
      const results = await Promise.all(promises);
      const picks = [];
      results.forEach(({ data }) => data.picks.filter((p) => p.user_id === uid).forEach((p) => picks.push(p)));
      setUserPicks(picks);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Load failed"));
    }
  };

  if (!group) return null;
  if (!board) return <div className="px-4 pt-4"><Skeleton rows={5} height={70} testId="ladder-skeleton" /></div>;

  const seasonWeeks = board.season_weeks || 10;
  const prize = board.prize_display;

  return (
    <div className="px-4 pt-4 space-y-4 pb-24" data-testid="ladder-tab-content">
      {/* View switcher */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex bg-white border border-[#e9ecef] rounded-full p-1" data-testid="ladder-view-toggle">
          <button
            onClick={() => { setView("season"); setWeek(null); }}
            className={`px-4 py-1.5 rounded-full text-xs font-bold transition-colors ${view === "season" ? "bg-[#1971c2] text-white" : "text-[#6c757d]"}`}
            data-testid="ladder-view-season"
          >Season</button>
          <button
            onClick={() => { setView("week"); setWeek((w) => w || 1); }}
            className={`px-4 py-1.5 rounded-full text-xs font-bold transition-colors ${view === "week" ? "bg-[#1971c2] text-white" : "text-[#6c757d]"}`}
            data-testid="ladder-view-week"
          >Weekly</button>
        </div>
        {flash && (
          <div className="lb-updated-flash inline-flex items-center gap-1 bg-[#2f9e44]/10 text-[#2f9e44] font-black text-[10px] uppercase tracking-wider rounded-full px-2 py-1 border border-[#2f9e44]/30" data-testid="ladder-updated-flash">
            <Zap className="w-3 h-3" strokeWidth={2.5} /> Updated
          </div>
        )}
      </div>

      {view === "week" && (
        <div className="flex gap-1.5 overflow-x-auto -mx-4 px-4" data-testid="ladder-week-chips">
          {Array.from({ length: seasonWeeks }, (_, i) => i + 1).map((n) => (
            <button
              key={n}
              onClick={() => setWeek(n)}
              data-testid={`ladder-week-${n}`}
              className={`shrink-0 rounded-full px-3 py-1 text-xs font-bold border transition-transform active:scale-95 ${
                week === n ? "bg-[#1971c2] text-white border-[#1971c2]" : "bg-white text-[#212529] border-[#e9ecef]"
              }`}
            >Wk {n}</button>
          ))}
        </div>
      )}

      {/* Prize pool */}
      {prize && (prize.prize_1st?.amount || prize.prize_2nd?.amount || prize.prize_3rd?.amount) && (
        <div className="bg-gradient-to-r from-[#c07800] to-[#e6a000] text-white rounded-2xl p-4 flex items-center gap-3 shadow-md" data-testid="ladder-prize-card">
          <Trophy className="w-6 h-6" strokeWidth={2.5} />
          <div className="text-xs font-black uppercase tracking-wider opacity-90">Prize pool</div>
          <div className="flex-1 flex justify-end gap-3 text-sm font-bold">
            {prize.prize_1st?.amount ? (<div>🥇 ${prize.prize_1st.amount}{prize.prize_1st.shared ? " (shared)" : ""}</div>) : null}
            {prize.prize_2nd?.amount ? (<div>🥈 ${prize.prize_2nd.amount}{prize.prize_2nd.shared ? " (shared)" : ""}</div>) : null}
            {prize.prize_3rd?.amount ? (<div>🥉 ${prize.prize_3rd.amount}{prize.prize_3rd.shared ? " (shared)" : ""}</div>) : null}
          </div>
        </div>
      )}

      {/* Podium */}
      <div className="rounded-2xl p-4 text-white" style={{ background: "linear-gradient(135deg, #1864ab 0%, #1971c2 100%)" }} data-testid="ladder-podium">
        <div className="text-[10px] font-black uppercase tracking-wider opacity-90 mb-2 text-center">
          {view === "season" ? "Season leaders" : `Week ${week ?? 1} leaders`}
        </div>
        <div className="flex items-end gap-2 min-h-[160px]">
          <PodiumColumn rank={2} entries={podium.r2} medal="🥈" heightClass="h-16" />
          <PodiumColumn rank={1} entries={podium.r1} medal="🥇" heightClass="h-24" />
          <PodiumColumn rank={3} entries={podium.r3} medal="🥉" heightClass="h-12" />
        </div>
      </div>

      {/* Per-race progress */}
      {dates.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-display font-extrabold text-lg">Race by race</h3>
            <select
              value={progressDate || ""}
              onChange={(e) => setProgressDate(e.target.value)}
              className="rounded-lg border border-[#e9ecef] text-xs font-bold py-1 px-2 bg-white"
              data-testid="progress-date-select"
            >
              {dates.map((d) => (
                <option key={d.meeting_date} value={d.meeting_date}>{d.meeting_date}</option>
              ))}
            </select>
          </div>
          {progress && progress.races.length > 0 ? (
            <div className="bg-white border border-[#e9ecef] rounded-2xl overflow-hidden">
              <div className="overflow-x-auto" data-testid="progress-table">
                <table className="min-w-full text-xs">
                  <thead className="bg-[#f8f9fa] text-[10px] uppercase tracking-wider text-[#6c757d]">
                    <tr>
                      <th className="text-left px-3 py-2 sticky left-0 bg-[#f8f9fa]">Member</th>
                      {progress.races.map((r) => (
                        <th key={r.id} className="px-3 py-2 whitespace-nowrap text-right">
                          {r.track.split(" ")[0]}·R{r.race_number}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {progress.progress.map((row) => (
                      <tr key={row.user_id} className="border-t border-[#e9ecef]" data-testid={`progress-row-${row.user_id}`}>
                        <td className="px-3 py-2 sticky left-0 bg-white flex items-center gap-2">
                          <span className="text-lg">{row.avatar_emoji}</span>
                          <span className="font-bold truncate max-w-[100px]">{row.display_name}</span>
                        </td>
                        {row.per_race.map((c, i) => (
                          <td key={i} className="px-3 py-2 text-right tabular-nums font-bold">
                            {c.cumulative != null ? fmtPts(c.cumulative) : "—"}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="bg-white border border-[#e9ecef] rounded-2xl p-4 text-center text-xs text-[#6c757d]" data-testid="progress-empty">
              No finished races yet on this date.
            </div>
          )}
        </div>
      )}

      {/* Standings */}
      <div>
        <h3 className="font-display font-extrabold text-lg mb-2">Standings</h3>
        <div className="space-y-2" data-testid="standings-list">
          {board.entries.map((e) => {
            const barPct = topPoints > 0 ? Math.max(6, Math.round((e.total_points / topPoints) * 100)) : 0;
            return (
              <div
                key={e.user_id}
                className="bg-white border border-[#e9ecef] rounded-2xl p-3 flex items-center gap-3"
                data-testid={`standings-row-${e.user_id}`}
              >
                <div className="text-xs font-black tabular-nums w-6 text-center text-[#6c757d]">
                  {e.rank}
                </div>
                <div className="w-10 h-10 rounded-full bg-[#f8f9fa] border border-[#e9ecef] flex items-center justify-center text-xl shrink-0">
                  {e.avatar_emoji}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <div className="font-display font-extrabold text-sm truncate">{e.display_name}</div>
                    {e.wooden_spoon && (
                      <span className="text-xs" title="Wooden spoon" data-testid={`wooden-spoon-${e.user_id}`}>🥄</span>
                    )}
                    {e.is_owner && <span className="text-[9px] font-black uppercase tracking-wider text-[#c07800]">Owner</span>}
                  </div>
                  <div className="h-1.5 mt-1 rounded-full bg-[#f8f9fa] overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${barPct}%`, backgroundColor: "#1971c2" }} />
                  </div>
                  <div className="text-[10px] text-[#6c757d] mt-0.5 font-bold">
                    {e.wins}W · {e.places}P · {e.unplaced}U · {e.wildcards_used}⚡
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-display font-black text-lg tabular-nums text-[#212529]">{fmtPts(e.total_points)}</div>
                  <div className="text-[9px] font-black uppercase tracking-widest text-[#6c757d]">pts</div>
                </div>
                <button
                  onClick={() => openUserPicks(e.user_id)}
                  className="w-8 h-8 rounded-full bg-[#f8f9fa] hover:bg-[#e9ecef] flex items-center justify-center active:scale-95 transition-transform"
                  data-testid={`view-picks-${e.user_id}`}
                  aria-label={`View ${e.display_name}'s picks`}
                >
                  <Eye className="w-4 h-4" strokeWidth={2.5} />
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Biggest Bolter */}
      <BolterCard bolter={bolter} onShare={() => shareBolter(bolter, group.name)} />

      {/* Standings user picks bottom-sheet */}
      <BottomSheet open={!!openUser} onClose={() => { setOpenUser(null); setUserPicks([]); }} title="Their picks" testId="user-picks-sheet">
        {userPicks.length === 0 ? (
          <div className="text-sm text-[#6c757d] text-center py-6">No picks yet.</div>
        ) : (
          <div className="space-y-2 max-h-[55vh] overflow-y-auto">
            {userPicks.map((p) => (
              <div key={p.id} className="border border-[#e9ecef] rounded-xl p-2.5 text-xs">
                <div className="flex items-center justify-between">
                  <span className="font-bold">Race pick #{p.horse_number}</span>
                  <span className="tabular-nums font-black">{p.points_earned != null ? fmtPts(p.points_earned) + " pts" : p.status}</span>
                </div>
                {p.wildcard_applied && <span className="text-[9px] font-black uppercase tracking-wider text-[#6741d9]">Wildcard ×2</span>}
              </div>
            ))}
          </div>
        )}
      </BottomSheet>
    </div>
  );
}

function BolterCard({ bolter, onShare }) {
  if (!bolter) {
    return (
      <div className="bg-white border border-[#e9ecef] rounded-2xl p-4 text-center" data-testid="bolter-empty">
        <div className="text-4xl mb-1">🕵️</div>
        <div className="font-display font-extrabold text-base">Still hunting for a bolter…</div>
        <p className="text-xs text-[#6c757d] mt-1">The longest-priced winning tip will appear here after the first result.</p>
      </div>
    );
  }
  const first = bolter.entries[0];
  const silk = SILK_COLOURS[((first.horse.horse_number || 1) - 1) % 12];
  return (
    <div className="rounded-2xl overflow-hidden border border-[#e9ecef] shadow-md" data-testid="bolter-card">
      <div className="p-4 text-white" style={{ background: "linear-gradient(135deg, #6741d9 0%, #9b6bff 100%)" }}>
        <div className="flex items-center justify-between">
          <div className="text-[10px] font-black uppercase tracking-widest opacity-90">Biggest bolter of the week</div>
          <button
            onClick={onShare}
            className="rounded-full bg-white/20 hover:bg-white/30 p-1.5"
            data-testid="bolter-share"
            aria-label="Share"
          >
            <Share2 className="w-4 h-4" strokeWidth={2.5} />
          </button>
        </div>
        <div className="mt-2 flex items-center gap-3">
          <div className="w-14 h-14 rounded-lg flex items-center justify-center text-white font-black text-2xl shrink-0" style={{ backgroundColor: silk, boxShadow: "inset 0 0 0 2px rgba(255,255,255,0.35)" }}>
            {first.horse.horse_number}
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-display font-black text-xl leading-tight truncate">{first.horse.name}</div>
            <div className="text-[11px] font-bold opacity-90 truncate">
              {first.race.track} · R{first.race.race_number} · {first.race.race_name}
            </div>
          </div>
          <div className="text-right">
            <div className="font-display font-black text-3xl tabular-nums leading-none">{bolter.odds.toFixed(2)}</div>
            <div className="text-[9px] font-black uppercase tracking-widest opacity-80">odds</div>
          </div>
        </div>
      </div>
      <div className="bg-white p-3 flex items-center gap-2 flex-wrap">
        {bolter.entries.map((e, i) =>
          e.pickers.map((p) => (
            <div key={`${i}-${p.user_id}`} className="flex items-center gap-1.5 bg-[#f8f9fa] rounded-full pl-1 pr-3 py-1 border border-[#e9ecef]" data-testid={`bolter-picker-${p.user_id}`}>
              <div className="w-6 h-6 rounded-full bg-white flex items-center justify-center text-sm">{p.avatar_emoji}</div>
              <span className="text-xs font-bold">{p.display_name}</span>
              {p.wildcard_applied && <span className="text-[9px] font-black uppercase text-[#6741d9]">⚡</span>}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

async function shareBolter(bolter, groupName) {
  if (!bolter) return;
  const first = bolter.entries[0];
  const names = bolter.entries.flatMap((e) => e.pickers.map((p) => p.display_name)).join(", ");
  const text = `🏇 Biggest Bolter — ${first.horse.name} @ ${bolter.odds.toFixed(2)} tipped by ${names} in ${groupName}. #HorseTips4fun`;
  if (navigator.share) { try { await navigator.share({ title: "Biggest Bolter", text }); return; } catch (_) {} }
  try { await navigator.clipboard.writeText(text); toast.success("Copied to clipboard"); } catch (_) {}
}
